// src/pages/InterviewAssistant/CaseInterview.tsx
import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Target, BarChart2, Award
} from 'lucide-react';
import {
  Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Tooltip
} from 'recharts';

// ------------------- данные компетенций и кейсов -------------------
const competencies = [
  {
    title: 'Аналитическое мышление',
    description: 'Способность структурировать информацию, выделять главное и находить причинно-следственные связи',
    cases: [
      {
        id: 1,
        text: 'Вы получили два противоречивых отчёта от разных отделов. Оба выглядят убедительно. Ваши действия?',
        options: [
          { value: 1, label: 'Приму тот, который кажется более правдоподобным' },
          { value: 2, label: 'Попрошу коллег высказать своё мнение' },
          { value: 3, label: 'Сравню методологию сбора данных в обоих отчётах и выявлю расхождения' },
          { value: 4, label: 'Проведу собственный анализ и сопоставлю с обоими отчётами' },
          { value: 5, label: 'Организую встречу с авторами отчётов для совместной верификации данных' }
        ]
      },
      {
        id: 2,
        text: 'Перед вами большой массив неструктурированных данных. Как вы подойдёте к его анализу?',
        options: [
          { value: 1, label: 'Попробую обработать всё в Excel' },
          { value: 2, label: 'Воспользуюсь известными мне инструментами визуализации' },
          { value: 3, label: 'Сначала определю ключевые вопросы, затем выберу метод анализа' },
          { value: 4, label: 'Структурирую данные, выделю значимые сегменты и построю гипотезы' },
          { value: 5, label: 'Применю системный подход: от первичной очистки до построения прогнозной модели' }
        ]
      }
    ]
  },
  {
    title: 'Принятие решений',
    description: 'Умение выбирать оптимальный вариант в условиях неопределённости и ограниченного времени',
    cases: [
      {
        id: 3,
        text: 'Вы должны принять важное решение, но у вас нет полной информации. Ваш подход?',
        options: [
          { value: 1, label: 'Отложу решение до получения всех данных' },
          { value: 2, label: 'Посоветуюсь с руководителем' },
          { value: 3, label: 'Оценю риски и выберу самый безопасный вариант' },
          { value: 4, label: 'Соберу максимум доступной информации и приму решение с учётом вероятностей' },
          { value: 5, label: 'Приму решение, основанное на анализе рисков и возможностей, с готовностью корректировать курс' }
        ]
      },
      {
        id: 4,
        text: 'Два предложенных решения кажутся одинаково хорошими. Как вы выберете?',
        options: [
          { value: 1, label: 'Подброшу монетку' },
          { value: 2, label: 'Выберу то, что проще реализовать' },
          { value: 3, label: 'Проведу SWOT-анализ для каждого варианта' },
          { value: 4, label: 'Привлеку экспертов и проведу взвешенное сравнение по критериям' },
          { value: 5, label: 'Смоделирую последствия каждого варианта и выберу наиболее устойчивый' }
        ]
      }
    ]
  },
  {
    title: 'Коммуникация',
    description: 'Эффективное донесение информации и умение слушать',
    cases: [
      {
        id: 5,
        text: 'Вам нужно объяснить сложную техническую концепцию коллеге из другого отдела. Ваши действия?',
        options: [
          { value: 1, label: 'Расскажу, как сам понимаю, без адаптации' },
          { value: 2, label: 'Использую профессиональные термины, чтобы звучать убедительно' },
          { value: 3, label: 'Приведу аналогии и примеры, понятные непрофессионалу' },
          { value: 4, label: 'Спрошу, что он уже знает, и построю объяснение от этого' },
          { value: 5, label: 'Проведу мини-воркшоп с визуализацией, интерактивом и обратной связью' }
        ]
      },
      {
        id: 6,
        text: 'Во время совещания возник конфликт мнений. Как вы будете способствовать его разрешению?',
        options: [
          { value: 1, label: 'Промолчу, чтобы не усугублять' },
          { value: 2, label: 'Поддержу ту сторону, которая мне ближе' },
          { value: 3, label: 'Предложу выслушать каждого и записать аргументы' },
          { value: 4, label: 'Выступлю модератором и помогу найти компромисс' },
          { value: 5, label: 'Использую техники фасилитации для конструктивного диалога и выработки общего решения' }
        ]
      }
    ]
  },
  {
    title: 'Управление конфликтами',
    description: 'Способность конструктивно разрешать разногласия и предотвращать эскалацию',
    cases: [
      {
        id: 7,
        text: 'Два ваших подчинённых открыто конфликтуют, и это влияет на работу отдела. Что вы сделаете?',
        options: [
          { value: 1, label: 'Сделаю вид, что ничего не происходит' },
          { value: 2, label: 'Строго укажу обоим на недопустимость конфликтов' },
          { value: 3, label: 'Поговорю с каждым по отдельности и выясню причины' },
          { value: 4, label: 'Соберу совместную встречу для выработки решения' },
          { value: 5, label: 'Проведу медиацию, помогу им найти взаимовыгодный выход и установлю правила взаимодействия' }
        ]
      },
      {
        id: 8,
        text: 'Клиент недоволен работой вашей команды и требует заменить руководителя проекта. Ваша реакция?',
        options: [
          { value: 1, label: 'Сразу соглашусь, чтобы не потерять клиента' },
          { value: 2, label: 'Буду защищать руководителя, не разбираясь в ситуации' },
          { value: 3, label: 'Встречусь с клиентом, выслушаю претензии и пообещаю разобраться' },
          { value: 4, label: 'Проведу внутреннее расследование, затем представлю клиенту план корректирующих действий' },
          { value: 5, label: 'Организую совместную встречу с клиентом и командой для восстановления доверия и поиска решений' }
        ]
      }
    ]
  },
  {
    title: 'Ориентация на клиента',
    description: 'Способность видеть ситуацию глазами клиента и предлагать решения, превосходящие ожидания',
    cases: [
      {
        id: 9,
        text: 'Клиент просит функцию, которая не предусмотрена текущим контрактом и технически сложна. Ваши действия?',
        options: [
          { value: 1, label: 'Откажу, сославшись на контракт' },
          { value: 2, label: 'Предложу платное дополнение' },
          { value: 3, label: 'Поищу компромиссный вариант' },
          { value: 4, label: 'Изучу потребность глубже и предложу альтернативное решение' },
          { value: 5, label: 'Найду способ реализовать запрос в рамках долгосрочного партнёрства, даже если придётся немного выйти за рамки' }
        ]
      },
      {
        id: 10,
        text: 'Вы заметили, что продукт не полностью удовлетворяет потребности клиента, хотя формально спецификация соблюдена. Что вы предпримете?',
        options: [
          { value: 1, label: 'Ничего, главное — соблюдение ТЗ' },
          { value: 2, label: 'Сообщу клиенту, что это его проблема' },
          { value: 3, label: 'Предложу доработку за дополнительную плату' },
          { value: 4, label: 'Инициирую обсуждение с клиентом и командой для улучшения продукта' },
          { value: 5, label: 'Выйду за рамки проекта, чтобы предложить клиенту максимальную ценность, и обосную это перед руководством' }
        ]
      }
    ]
  },
  {
    title: 'Работа в команде',
    description: 'Эффективное взаимодействие, взаимопомощь и достижение синергии',
    cases: [
      {
        id: 11,
        text: 'Ваш коллега не справляется со своей частью проекта, и это ставит под угрозу общий срок. Ваши действия?',
        options: [
          { value: 1, label: 'Сделаю его работу сам, чтобы не сорвать дедлайн' },
          { value: 2, label: 'Пожалуюсь руководителю' },
          { value: 3, label: 'Предложу помощь и вместе спланируем дальнейшие шаги' },
          { value: 4, label: 'Выясню причины и помогу найти решение, не обвиняя' },
          { value: 5, label: 'Организую командный брейншторм по перераспределению задач, сохраняя позитивную атмосферу' }
        ]
      },
      {
        id: 12,
        text: 'В новом проекте у вас нет явного лидера. Как вы будете выстраивать работу?',
        options: [
          { value: 1, label: 'Буду ждать, пока кто-то возьмёт на себя руководство' },
          { value: 2, label: 'Возьму лидерство на себя, даже если не уверен' },
          { value: 3, label: 'Предложу распределить роли на первой встрече' },
          { value: 4, label: 'Инициирую обсуждение сильных сторон каждого и совместно определим формат работы' },
          { value: 5, label: 'Создам самоорганизующуюся команду с чёткими целями и взаимной поддержкой' }
        ]
      }
    ]
  },
  {
    title: 'Лидерство',
    description: 'Способность вдохновлять, вести за собой и принимать ответственность',
    cases: [
      {
        id: 13,
        text: 'Команда потеряла мотивацию после провала пилотного запуска. Как вы вернёте боевой дух?',
        options: [
          { value: 1, label: 'Скажу, что это был ожидаемый результат' },
          { value: 2, label: 'Устрою разбор полётов с критикой' },
          { value: 3, label: 'Проведу встречу, чтобы обсудить уроки и отметить положительные моменты' },
          { value: 4, label: 'Предложу конкретный план исправления и поддержу каждого лично' },
          { value: 5, label: 'Превращу неудачу в трамплин для роста, вдохновив команду на новую попытку с учётом опыта' }
        ]
      },
      {
        id: 14,
        text: 'Вы стали руководителем отдела, где раньше были рядовым сотрудником. Как выстроите отношения с бывшими коллегами?',
        options: [
          { value: 1, label: 'Постараюсь сохранить прежние дружеские отношения' },
          { value: 2, label: 'Сразу обозначу жёсткую субординацию' },
          { value: 3, label: 'Проведу индивидуальные беседы и объясню свои ожидания' },
          { value: 4, label: 'Буду сочетать профессионализм с доступностью, постепенно выстраивая авторитет' },
          { value: 5, label: 'Создам атмосферу уважения и партнёрства, оставаясь справедливым и последовательным лидером' }
        ]
      }
    ]
  },
  {
    title: 'Стрессоустойчивость',
    description: 'Способность сохранять продуктивность и ясность мышления в напряжённых ситуациях',
    cases: [
      {
        id: 15,
        text: 'За час до важной презентации вы обнаруживаете критическую ошибку в данных. Ваши действия?',
        options: [
          { value: 1, label: 'Паникую и переношу презентацию' },
          { value: 2, label: 'Быстро исправляю, что успею, и надеюсь, что не заметят' },
          { value: 3, label: 'Связываюсь с командой для экстренного исправления' },
          { value: 4, label: 'Спокойно оцениваю масштаб ошибки и готовлю честное объяснение с планом исправления' },
          { value: 5, label: 'Использую ошибку как возможность показать, как мы оперативно решаем проблемы, и провожу презентацию с учётом корректировок' }
        ]
      },
      {
        id: 16,
        text: 'Вы работаете над несколькими срочными задачами одновременно, и сроки поджимают. Как вы справитесь?',
        options: [
          { value: 1, label: 'Буду метаться между задачами' },
          { value: 2, label: 'Попрошу перенести дедлайны' },
          { value: 3, label: 'Расставлю приоритеты и составлю жёсткий график' },
          { value: 4, label: 'Делегирую часть задач и сосредоточусь на ключевых' },
          { value: 5, label: 'Сохраняю спокойствие, быстро переключаюсь, контролирую прогресс и при необходимости пересматриваю план' }
        ]
      }
    ]
  }
];

const interpretation: Record<string, { low: string; medium: string; high: string }> = {
  'Аналитическое мышление': {
    low: 'Развивайте навыки структурирования информации и критического анализа. Рекомендуются курсы по Data Analysis и системному мышлению.',
    medium: 'Вы обладаете базовыми аналитическими способностями, но в сложных ситуациях может не хватать глубины. Практикуйте разбор кейсов.',
    high: 'Отличное аналитическое мышление. Вы способны быстро обрабатывать большие объёмы данных и принимать обоснованные решения.'
  },
  'Принятие решений': {
    low: 'Старайтесь не избегать ответственности за выбор. Тренируйтесь принимать решения в условиях неопределённости.',
    medium: 'Вы принимаете решения, но иногда излишне осторожны. Учитесь доверять своей интуиции и расчётам.',
    high: 'Решительность и взвешенность — ваши сильные стороны. Вы умеете находить баланс между скоростью и обоснованностью.'
  },
  'Коммуникация': {
    low: 'Работайте над ясностью изложения и активным слушанием. Полезны тренинги по презентациям и переговорам.',
    medium: 'Вы можете донести мысль, но есть потенциал для улучшения в сложных коммуникациях. Практикуйте фасилитацию.',
    high: 'Высокий уровень коммуникативных навыков. Вы легко находите общий язык с любой аудиторией и умеете управлять диалогом.'
  },
  'Управление конфликтами': {
    low: 'Избегание конфликтов — не выход. Развивайте навыки медиации и конструктивного разрешения разногласий.',
    medium: 'Вы способны урегулировать конфликты, но не всегда проактивны. Учитесь предотвращать эскалацию.',
    high: 'Вы прирождённый медиатор. Умеете превращать конфликты в возможности для улучшения отношений и процессов.'
  },
  'Ориентация на клиента': {
    low: 'Старайтесь чаще смотреть на ситуацию глазами клиента. Спрашивайте обратную связь и изучайте его потребности.',
    medium: 'Вы учитываете интересы клиента, но иногда формальный подход мешает видеть глубинные потребности.',
    high: 'Клиентоориентированность — ваше конкурентное преимущество. Вы создаёте выдающийся клиентский опыт.'
  },
  'Работа в команде': {
    low: 'Развивайте навыки коллаборации. Участвуйте в командных проектах и учитесь доверять коллегам.',
    medium: 'Вы командный игрок, но можете проявлять индивидуализм. Больше вовлекайтесь в совместную работу.',
    high: 'Вы отличный командный игрок. С вами комфортно работать, вы цените вклад каждого и умеете достигать синергии.'
  },
  'Лидерство': {
    low: 'Лидерские качества можно развить. Начните с малого: возьмите на себя ответственность за небольшой проект.',
    medium: 'У вас есть лидерский потенциал, но не хватает уверенности. Берите инициативу и учитесь вдохновлять других.',
    high: 'Вы прирождённый лидер. Люди идут за вами, потому что вы ведёте за собой, а не просто управляете.'
  },
  'Стрессоустойчивость': {
    low: 'Работайте над управлением стрессом: дыхательные практики, планирование, делегирование.',
    medium: 'Вы справляетесь со стрессом, но он может снижать вашу эффективность. Учитесь сохранять спокойствие в любых условиях.',
    high: 'Высокая стрессоустойчивость помогает вам оставаться продуктивным даже в самых напряжённых ситуациях.'
  }
};

// ------------------- компонент -------------------
const CaseInterview: React.FC = () => {
  const navigate = useNavigate();
  const [currentCompetencyIndex, setCurrentCompetencyIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<number, number>>({});
  const [submitted, setSubmitted] = useState(false);

  const allCases = useMemo(() => competencies.flatMap(c => c.cases), []);

  const handleAnswer = (caseId: number, value: number) => {
    setAnswers(prev => ({ ...prev, [caseId]: value }));
  };

  const currentCompetency = competencies[currentCompetencyIndex];
  const currentCases = currentCompetency.cases;
  const allAnswered = allCases.every(c => answers[c.id] !== undefined);

  const goNext = () => {
    if (currentCompetencyIndex < competencies.length - 1) {
      setCurrentCompetencyIndex(prev => prev + 1);
    }
  };

  const goPrev = () => {
    if (currentCompetencyIndex > 0) {
      setCurrentCompetencyIndex(prev => prev - 1);
    }
  };

  const handleSubmit = () => setSubmitted(true);

  const scoresByCompetency = useMemo(() => {
    return competencies.map(comp => {
      const total = comp.cases.reduce((sum, c) => sum + (answers[c.id] || 0), 0);
      const max = comp.cases.length * 5;
      const percentage = Math.round((total / max) * 100);
      return { title: comp.title, percentage, total };
    });
  }, [answers]);

  const radarData = scoresByCompetency.map(s => ({
    competency: s.title,
    score: s.percentage,
    fullMark: 100,
  }));

  // ---------- страница результатов ----------
  if (submitted) {
    return (
      <div style={styles.container}>
        <div style={styles.bgBlur1} />
        <div style={styles.bgBlur2} />

        <button onClick={() => navigate('/app/interview-assistant')} style={styles.backButton}>
          <ArrowLeft size={18} />
          <span>К помощнику интервью</span>
        </button>

        <div style={styles.header}>
          <h1 style={styles.title}>Результаты кейс‑интервью</h1>
          <p style={styles.subtitle}>Профиль компетенций на основе решения ситуационных задач</p>
        </div>

        <div style={styles.resultsGrid}>
          <div style={styles.resultCard}>
            <h3 style={styles.chartTitle}><BarChart2 size={18} /> Профиль компетенций</h3>
            <ResponsiveContainer width="100%" height={350}>
              <RadarChart data={radarData} outerRadius={120}>
                <PolarGrid strokeDasharray="3 3" />
                <PolarAngleAxis dataKey="competency" tick={{ fontSize: 11 }} />
                <PolarRadiusAxis angle={30} domain={[0, 100]} tick={false} />
                <Radar isAnimationActive={false} name="Уровень" dataKey="score" stroke="#e67e22" fill="#e67e22" fillOpacity={0.2} />
                <Tooltip />
              </RadarChart>
            </ResponsiveContainer>
          </div>

          <div style={styles.resultCard}>
            <h3 style={styles.chartTitle}><Award size={18} /> Детализация по компетенциям</h3>
            {scoresByCompetency.map(s => {
              const interp = interpretation[s.title];
              const level = s.percentage >= 70 ? 'high' : s.percentage >= 40 ? 'medium' : 'low';
              const color = level === 'high' ? '#2ecc71' : level === 'medium' ? '#f39c12' : '#e74c3c';
              return (
                <div key={s.title} style={styles.competencyRow}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <strong style={{ fontSize: 15 }}>{s.title}</strong>
                    <span style={{ color, fontWeight: 600 }}>{s.percentage}%</span>
                  </div>
                  <div style={styles.progressBar}>
                    <div style={{ ...styles.progressFill, width: `${s.percentage}%`, background: color }} />
                  </div>
                  <p style={styles.interpretationText}>
                    {level === 'high' && interp.high}
                    {level === 'medium' && interp.medium}
                    {level === 'low' && interp.low}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        <button
          onClick={() => { setSubmitted(false); setAnswers({}); setCurrentCompetencyIndex(0); }}
          style={styles.resetButton}
        >
          Пройти заново
        </button>
      </div>
    );
  }

  // ---------- кейсы ----------
  return (
    <div style={styles.container}>
      <div style={styles.bgBlur1} />
      <div style={styles.bgBlur2} />

      <button onClick={() => navigate('/app/interview-assistant')} style={styles.backButton}>
        <ArrowLeft size={18} />
        <span>К помощнику интервью</span>
      </button>

      <div style={styles.header}>
        <h1 style={styles.title}>Кейс‑интервью (ситуационное)</h1>
        <p style={styles.subtitle}>
          Решите предложенные ситуации, чтобы мы оценили ваши компетенции в действии.
        </p>
      </div>

      <div style={styles.progressIndicator}>
        {competencies.map((_, idx) => (
          <div
            key={idx}
            style={{
              ...styles.dot,
              background: idx <= currentCompetencyIndex ? '#e67e22' : '#e0e0e0',
            }}
          />
        ))}
      </div>

      <div style={styles.card}>
        <h2 style={styles.competencyTitle}>
          <Target size={20} /> {currentCompetency.title}
        </h2>
        <p style={styles.competencyDesc}>{currentCompetency.description}</p>

        {currentCases.map(kase => (
          <div key={kase.id} style={styles.caseBlock}>
            <p style={styles.caseText}>{kase.text}</p>
            <div style={styles.optionsGrid}>
              {kase.options.map(opt => (
                <button
                  key={opt.value}
                  style={{
                    ...styles.optionButton,
                    background: answers[kase.id] === opt.value ? 'linear-gradient(135deg, #e67e22, #f39c12)' : 'rgba(255,255,255,0.5)',
                    color: answers[kase.id] === opt.value ? 'white' : '#1a1a2e',
                    border: answers[kase.id] === opt.value ? 'none' : '1px solid rgba(0,0,0,0.1)',
                  }}
                  onClick={() => handleAnswer(kase.id, opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        ))}

        <div style={styles.navButtons}>
          <button onClick={goPrev} disabled={currentCompetencyIndex === 0} style={styles.navButton}>
            Назад
          </button>
          <span style={{ color: '#666' }}>
            {currentCompetencyIndex + 1} / {competencies.length}
          </span>
          {currentCompetencyIndex < competencies.length - 1 ? (
            <button onClick={goNext} style={styles.navButton}>Далее</button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={!allAnswered}
              style={{
                ...styles.navButton,
                background: allAnswered ? 'linear-gradient(135deg, #2ecc71, #27ae60)' : '#ccc',
                color: 'white',
                border: 'none',
              }}
            >
              Завершить и получить результат
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ------------------- стили -------------------
const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '80px 20px 20px 20px',
    background: 'linear-gradient(145deg, #f8fafd 0%, #e8f0fe 100%)',
    fontFamily: '"Inter", "Segoe UI", Tahoma, Geneva, Verdana, sans-serif',
    position: 'relative',
    overflowX: 'clip',                     // вместо 'hidden', чтобы не создавать контекст скролла
    willChange: 'transform',              // ускоряет композитинг
    transform: 'translateZ(0)',           // создаёт отдельный слой

  },
  bgBlur1: {
    position: 'absolute', top: '-10%', left: '-5%', width: 600, height: 600,
    background: 'radial-gradient(circle, rgba(106,17,203,0.12) 0%, rgba(106,17,203,0) 70%)',
    borderRadius: '50%', filter: 'blur(60px)', zIndex: 0,
  },
  bgBlur2: {
    position: 'absolute', bottom: '-10%', right: '-5%', width: 700, height: 700,
    background: 'radial-gradient(circle, rgba(37,117,252,0.12) 0%, rgba(37,117,252,0) 70%)',
    borderRadius: '50%', filter: 'blur(60px)', zIndex: 0,
  },
  backButton: {
    position: 'absolute', top: 24, left: 24,
    background: 'rgba(255,255,255,0.6)',
    border: '1px solid rgba(0,0,0,0.06)',
    borderRadius: 40,
    padding: '10px 20px', fontSize: 15, fontWeight: 500, color: '#2575fc',
    cursor: 'pointer', transition: 'all 0.2s ease', zIndex: 10, display: 'inline-flex', alignItems: 'center', gap: 8,
  },
  header: { textAlign: 'center', marginBottom: 32, position: 'relative', zIndex: 1 },
  title: {
    fontSize: 36, fontWeight: 700, background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', margin: '0 0 8px',
  },
  subtitle: { fontSize: 16, color: '#666', margin: 0 },
  progressIndicator: { display: 'flex', justifyContent: 'center', gap: 8, marginBottom: 24, position: 'relative', zIndex: 1 },
  dot: { width: 12, height: 12, borderRadius: '50%', transition: 'background 0.3s' },
  card: {
    background: 'rgba(255,255,255,0.5)', borderRadius: 32,
    padding: '40px 32px', border: '1px solid rgba(0,0,0,0.06)',
    maxWidth: 800, margin: '0 auto', position: 'relative', zIndex: 1,
  },
  competencyTitle: { fontSize: 24, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, color: '#1a1a2e' },
  competencyDesc: { fontSize: 15, color: '#4a4a68', marginBottom: 28 },
  caseBlock: { marginBottom: 32 },
  caseText: { fontSize: 16, fontWeight: 500, marginBottom: 12, color: '#1a1a2e', lineHeight: 1.5 },
  optionsGrid: { display: 'flex', flexDirection: 'column', gap: 10 },
  optionButton: {
    textAlign: 'left' as const, padding: '14px 20px', borderRadius: 16, cursor: 'pointer',
    transition: 'all 0.2s', fontSize: 14, border: '1px solid rgba(0,0,0,0.1)',
  },
  navButtons: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 32 },
  navButton: {
    padding: '10px 24px', borderRadius: 30, border: '1px solid #2575fc', background: 'transparent',
    color: '#2575fc', cursor: 'pointer', fontWeight: 600, transition: 'all 0.2s',
  },
  resultsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))', gap: 28, maxWidth: 1100, margin: '0 auto', position: 'relative', zIndex: 1 },
  resultCard: {
    background: 'rgba(255,255,255,0.5)', borderRadius: 32,
    padding: 32, border: '1px solid rgba(0,0,0,0.06)',
  },
  chartTitle: { fontSize: 20, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20, color: '#1a1a2e' },
  competencyRow: { marginBottom: 20 },
  progressBar: { height: 8, background: '#e0e0e0', borderRadius: 4, marginTop: 6, overflow: 'hidden' },
  progressFill: { height: '100%', borderRadius: 4, transition: 'width 0.3s' },
  interpretationText: { fontSize: 13, color: '#555', marginTop: 6, lineHeight: 1.5 },
  resetButton: {
    marginTop: 40, display: 'block', marginLeft: 'auto', marginRight: 'auto',
    padding: '12px 28px', borderRadius: 30, background: 'linear-gradient(135deg, #6a11cb, #2575fc)',
    color: 'white', border: 'none', cursor: 'pointer', fontWeight: 600,
  },
};

export default CaseInterview;